// src/pages/sales/PIDetailModal.jsx
import { useState, useEffect } from "react";
import { downloadPdf, fetchPiRevisions } from "../../apiService";
import { fmtDate } from "../../utils/dateUtils";

const STATUS_OPTIONS = ["DRAFT", "SENT", "ACCEPTED", "REJECTED", "EXPIRED"];

const STATUS_STYLE = {
  DRAFT:    { bg: "#f3f4f6", color: "#374151" },
  SENT:     { bg: "#dbeafe", color: "#1e40af" },
  ACCEPTED: { bg: "#d1fae5", color: "#065f46" },
  REJECTED: { bg: "#fee2e2", color: "#991b1b" },
  EXPIRED:  { bg: "#fef3c7", color: "#92400e" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.DRAFT;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
      {status}
    </span>
  );
}

function fmt(n) {
  const num = parseFloat(n);
  if (isNaN(num)) return "—";
  return num.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const INCOTERM_LABELS = {
  EXW: "EXW — Ex-Warehouse",
  DAP: "DAP — Delivered at Place",
  DDP: "DDP — Delivered Duty Paid",
  FOB: "FOB — Free on Board",
  CFR: "CFR — Cost and Freight",
  CIF: "CIF — Cost, Insurance & Freight",
};

function RevisionChange({ change }) {
  const s = { fontSize: 11, display: "flex", gap: 6, alignItems: "baseline", marginBottom: 2 };
  if (change.kind === "field") return (
    <div style={s}>
      <span style={{ color: "#6b7280" }}>{change.label}:</span>
      <span style={{ textDecoration: "line-through", color: "#dc2626" }}>{change.from || "—"}</span>
      <span style={{ color: "#6b7280" }}>→</span>
      <span style={{ color: "#059669" }}>{change.to || "—"}</span>
    </div>
  );
  if (change.kind === "line_added") return (
    <div style={s}>
      <span style={{ color: "#059669", fontWeight: 600 }}>+ Added:</span>
      <span>{change.product}</span>
    </div>
  );
  if (change.kind === "line_removed") return (
    <div style={s}>
      <span style={{ color: "#dc2626", fontWeight: 600 }}>− Removed:</span>
      <span style={{ textDecoration: "line-through", color: "#6b7280" }}>{change.product}</span>
    </div>
  );
  if (change.kind === "line_qty") return (
    <div style={s}>
      <span style={{ color: "#6b7280" }}>{change.product} — Qty:</span>
      <span style={{ textDecoration: "line-through", color: "#dc2626" }}>{change.from}</span>
      <span style={{ color: "#6b7280" }}>→</span>
      <span style={{ color: "#059669" }}>{change.to}</span>
    </div>
  );
  if (change.kind === "line_price") return (
    <div style={s}>
      <span style={{ color: "#6b7280" }}>{change.product} — Price:</span>
      <span style={{ textDecoration: "line-through", color: "#dc2626" }}>{change.from}</span>
      <span style={{ color: "#6b7280" }}>→</span>
      <span style={{ color: "#059669" }}>{change.to}</span>
    </div>
  );
  return null;
}

export function PIDetailModal({ pi, canCreate, bankAccounts, onStatusChange, onEdit, onClose }) {
  const [statusChanging, setStatusChanging] = useState(false);
  const [revisions,      setRevisions]      = useState(null);
  const [revOpen,        setRevOpen]        = useState(false);

  useEffect(() => {
    fetchPiRevisions(pi.id)
      .then(setRevisions)
      .catch(() => setRevisions([]));
  }, [pi.id]);

  const currency   = pi.currency || "AED";
  const lines      = pi.lines || [];
  const subtotal   = lines.reduce((s, l) => s + Number(l.quantity) * Number(l.unitPrice), 0);
  const vatAmount  = pi.vatEnabled ? subtotal * 0.05 : 0;
  const grandTotal = subtotal + vatAmount;
  const companyLabel = pi.piNumber?.startsWith("AMIP") ? "AMI — Industries" : "AMT — Trading";

  const bank = bankAccounts && pi.bankAccountId
    ? bankAccounts.find(b => b.id === pi.bankAccountId)
    : null;

  async function handleStatus(status) {
    setStatusChanging(true);
    try { await onStatusChange(pi.id, status); }
    finally { setStatusChanging(false); }
  }

  return (
    <div className="modal" style={{ width: 720, maxHeight: "90vh", overflowY: "auto" }}>
      <div className="modal-header">
        <div className="modal-title" style={{ fontFamily: "monospace" }}>{pi.piNumber}</div>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="modal-body">

        {/* Header row */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#888" }}>Customer</div>
            <div style={{ fontWeight: 600 }}>{pi.customerName}</div>
            {pi.shipToName    && <div style={{ fontSize: 12, color: "#555" }}>{pi.shipToName}</div>}
            {pi.addressedTo   && <div style={{ fontSize: 12 }}>Kind Attn: {pi.addressedTo}</div>}
            {pi.contactPerson && <div style={{ fontSize: 12 }}>Contact: {pi.contactPerson}</div>}
            {pi.reference     && <div style={{ fontSize: 12, color: "#888" }}>Ref: {pi.reference}</div>}
            {pi.quotationId   && (
              <div style={{ fontSize: 11, color: "#92400e", marginTop: 4 }}>
                Linked to quotation #{pi.quotationId}
              </div>
            )}
          </div>
          <div style={{ minWidth: 160 }}>
            <div style={{ fontSize: 11, color: "#888" }}>Date</div>
            <div style={{ fontWeight: 600 }}>{pi.date ? fmtDate(pi.date) : "—"}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Company</div>
            <div style={{ fontSize: 12 }}>{companyLabel}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Currency</div>
            <div style={{ fontSize: 12 }}>{currency}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Prepared By</div>
            <div style={{ fontSize: 12 }}>{pi.preparedBy}</div>
          </div>
        </div>

        {/* Line items */}
        <table className="data-table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th style={{ width: 32 }}>No.</th>
              <th>Description</th>
              <th style={{ textAlign: "right", width: 70 }}>Qty</th>
              <th style={{ width: 50 }}>UOM</th>
              <th style={{ width: 100 }}>Packing</th>
              <th style={{ textAlign: "right", width: 90 }}>Price ({currency})</th>
              <th style={{ textAlign: "right", width: 100 }}>Amount ({currency})</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.id ?? i}>
                <td style={{ textAlign: "center" }}>{i + 1}</td>
                <td>
                  <span style={{ fontWeight: 600 }}>{l.productName}</span>
                  {l.productCode && <span style={{ fontSize: 11, color: "#888" }}> ({l.productCode})</span>}
                </td>
                <td style={{ textAlign: "right" }}>{Number(l.quantity).toFixed(3)}</td>
                <td>{l.uom}</td>
                <td style={{ color: l.packingSize ? "#374151" : "#d1d5db" }}>{l.packingSize || "—"}</td>
                <td style={{ textAlign: "right" }}>{Number(l.unitPrice).toFixed(4)}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {fmt(Number(l.quantity) * Number(l.unitPrice))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ textAlign: "right", fontSize: 12, paddingRight: 4, marginBottom: 12 }}>
          <div>Sub Total: <strong>{currency} {fmt(subtotal)}</strong></div>
          {pi.vatEnabled  && <div>VAT 5%: <strong>{currency} {fmt(vatAmount)}</strong></div>}
          {!pi.vatEnabled && <div style={{ color: "#888", fontSize: 11 }}>*VAT if applicable</div>}
          <div style={{ fontSize: 15, marginTop: 4 }}>Total: <strong>{currency} {fmt(grandTotal)}</strong></div>
        </div>

        {/* Terms */}
        {(pi.paymentTerms || pi.incoterm || pi.notes) && (
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
            borderRadius: 6, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Terms &amp; Conditions</div>
            {pi.paymentTerms && <div><span style={{ fontWeight: 600 }}>Payment: </span>{pi.paymentTerms}</div>}
            {pi.incoterm     && <div><span style={{ fontWeight: 600 }}>Delivery: </span>{INCOTERM_LABELS[pi.incoterm] || pi.incoterm}</div>}
            {pi.notes        && <div style={{ marginTop: 4 }}><span style={{ fontWeight: 600 }}>Note: </span>{pi.notes}</div>}
          </div>
        )}

        {/* Bank details */}
        {bank && (
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
            borderRadius: 6, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 4, color: "#1a3a6b" }}>Bank Details</div>
            <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: "3px 8px" }}>
              <span style={{ color: "#6b7280" }}>Account Name</span><span>{bank.accountName}</span>
              <span style={{ color: "#6b7280" }}>Bank</span><span>{bank.bankName}</span>
              <span style={{ color: "#6b7280" }}>Branch</span><span>{bank.branchName}</span>
              <span style={{ color: "#6b7280" }}>Account No.</span>
              <span style={{ fontFamily: "monospace" }}>{bank.accountNumber}</span>
              <span style={{ color: "#6b7280" }}>IBAN</span>
              <span style={{ fontFamily: "monospace" }}>{bank.ibanNumber}</span>
              <span style={{ color: "#6b7280" }}>Swift / BIC</span>
              <span style={{ fontFamily: "monospace" }}>{bank.swiftCode}</span>
            </div>
          </div>
        )}

        {/* Status */}
        {canCreate && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Update Status</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {STATUS_OPTIONS.map(s => (
                <button key={s}
                  className={pi.status === s ? "btn btn-primary" : "btn"}
                  style={{ fontSize: 11 }}
                  disabled={statusChanging || pi.status === s}
                  onClick={() => handleStatus(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Revision History */}
        {revisions !== null && (
          <div style={{ marginBottom: 12 }}>
            <button
              style={{ background: "none", border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: 700, color: "#440099", padding: 0,
                display: "flex", alignItems: "center", gap: 4 }}
              onClick={() => setRevOpen(o => !o)}>
              {revOpen ? "▾" : "▸"} Revision History
              {revisions.length > 0 && (
                <span style={{ background: "rgba(68,0,153,0.1)", color: "#440099",
                  borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>
                  {revisions.length}
                </span>
              )}
            </button>
            {revOpen && (
              <div style={{ marginTop: 8 }}>
                {revisions.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#aaa" }}>No revisions recorded.</div>
                ) : revisions.map(rev => (
                  <div key={rev.revisionNo} style={{
                    border: "1px solid rgba(68,0,153,0.1)", borderRadius: 8,
                    padding: "8px 12px", marginBottom: 8,
                    background: "rgba(237,232,255,0.35)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      fontSize: 11, fontWeight: 700, color: "#440099", marginBottom: 4 }}>
                      <span>Revision {rev.revisionNo}</span>
                      <span style={{ fontWeight: 400, color: "#6b7280" }}>
                        {rev.changedAt ? rev.changedAt.replace("T", " ").slice(0, 16) : ""} · {rev.changedBy}
                      </span>
                    </div>
                    {(rev.changes || []).length === 0 ? (
                      <div style={{ fontSize: 12, color: "#aaa" }}>No tracked changes.</div>
                    ) : (rev.changes || []).map((c, i) => (
                      <RevisionChange key={i} change={c} />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="form-footer" style={{ justifyContent: "space-between" }}>
          <div>
            {canCreate && (pi.status === "DRAFT" || pi.status === "SENT") && (
              <button className="btn" onClick={() => onEdit(pi)}>Edit</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={onClose}>Close</button>
            <button className="btn btn-primary"
              onClick={() => downloadPdf(
                `/api/crm/proforma-invoices/${pi.id}/pdf`,
                pi.piNumber.replace(/\//g, "-") + ".pdf"
              )}>Download PDF</button>
          </div>
        </div>

      </div>
    </div>
  );
}
